import { el } from '../../dom';
import { playSfx } from '../../audio/audio';
import { chromeBottom } from '../../ui/panel';

/**
 * The game's own "are you sure?" card.
 *
 * OWNER (2026-08-17): "we make ingame confirmation, not via chrome."
 *
 * window.confirm() is wrong here for reasons past looks. It is chrome-styled and
 * carries the site's origin, it cannot be themed, on a phone it arrives as a
 * system sheet on top of a pixel-art game — and it BLOCKS THE MAIN THREAD, so the
 * farm's ticker stops dead and the world freezes mid-animation behind it while the
 * player reads.
 *
 * Two dialogs already did this properly (confirmDemolish, and the bed-loss card
 * this was factored out of). Two more were still calling the browser: sending a
 * villager away and releasing an animal — both irreversible, both about something
 * the player paid coins for. One helper now, so the next irreversible action has
 * no excuse to reach for the browser.
 */

export interface ConfirmOptions {
  title: string;
  /** The consequence, in plain words. Extra lines are smaller print. */
  body: string;
  detail?: string;
  /** Label on the destructive button. */
  confirmLabel: string;
  /** Label on the safe one. Defaults to "Cancel". */
  cancelLabel?: string;
  onConfirm: () => void;
  /**
   * Called when the card closes WITHOUT confirming — Cancel, Escape, a click on
   * the backdrop, or another dialog replacing this one.
   *
   * Optional because most callers only care about the yes: closing is the same as
   * never having opened it. A caller that is awaiting an answer does care, though
   * — without this, a player who presses Escape leaves it waiting forever (the
   * shell's tab buttons stop working, which is worse than the dialog itself).
   */
  onCancel?: () => void;
}

let overlay: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;
let armTimer = 0;
/** Fires once if the open card closes without a confirm. Cleared as it fires. */
let pendingCancel: (() => void) | null = null;

export function closeConfirm(): void {
  window.clearTimeout(armTimer);
  if (escHandler) { window.removeEventListener('keydown', escHandler, true); escHandler = null; }
  overlay?.remove();
  overlay = null;
  // Taken before calling: an onCancel that opens another card must not re-enter
  // this and fire itself a second time.
  const cancel = pendingCancel;
  pendingCancel = null;
  cancel?.();
}

/**
 * ARMING DELAY — the reason these cards are safe to open from a tap handler.
 *
 * The overlay is created synchronously inside the pointer handler and its button
 * lands where the player just tapped. On a canvas where everything is a tap, the
 * second click of a natural double-tap would otherwise land straight on the
 * destructive button and do the thing the dialog exists to prevent. 400ms is
 * comfortably longer than a double-click interval and short enough not to feel
 * broken; the button says so by being visibly disabled until then.
 */
const ARM_MS = 400;

export function openConfirm(opts: ConfirmOptions): void {
  closeConfirm();                    // resolves any previous card as a cancel
  playSfx('ui_open');
  pendingCancel = opts.onCancel ?? null;

  const armedAt = Date.now() + ARM_MS;
  const danger = el('button',
    'w-full min-h-[44px] px-3 py-2 mb-2 rounded bg-error/20 border border-error text-error hover:bg-error/30 opacity-50 pointer-events-none',
    // Belt and braces: the class comes off on the timer, but a click that reaches
    // us early (a synthetic event, a stale frame) is still refused by the clock.
    { onClick: () => {
      if (Date.now() < armedAt) return;
      pendingCancel = null;          // confirmed, so the close below is not a cancel
      closeConfirm();
      opts.onConfirm();
    } },
    opts.confirmLabel);
  armTimer = window.setTimeout(() => {
    danger.classList.remove('opacity-50', 'pointer-events-none');
  }, ARM_MS);

  const cancel = el('button', 'w-full min-h-[44px] px-3 py-2 rounded border border-border text-text-dim hover:text-text',
    { onClick: () => closeConfirm() }, opts.cancelLabel ?? 'Cancel');

  const card = el('div',
    'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card', {},
    el('h3', 'font-heading text-gold mb-1', {}, opts.title),
    el('p', 'text-sm text-text-dim mb-2', {}, opts.body),
    ...(opts.detail ? [el('p', 'text-[11px] text-text-dim mb-3', {}, opts.detail)] : []),
    danger, cancel);

  const top = chromeBottom() + 8;
  overlay = el('div', 'fixed inset-x-0 bottom-0 z-[70] flex items-start justify-center',
    { style: `top:${top}px`, onClick: (e) => { if (e.target === overlay) closeConfirm(); } },
    el('div', 'mt-[8vh]', {}, card));
  document.body.appendChild(overlay);
  cancel.focus();                    // the safe option is what the keyboard lands on
  escHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();             // not "close the panel behind me" as well
    closeConfirm();
  };
  window.addEventListener('keydown', escHandler, true);
}
